/**
 * Fixed set of fields the "Organiser" section of `item.extra_json` always
 * carries. The section itself isn't tagged — it's identified by containing
 * a field whose `field_hint` is `'Organiser'` (see `ORGANISER_SECTION_MARKER_HINT`),
 * the same way `getLocation()` identifies the venue section via `'Venue'`.
 */
export interface OrganiserDetails {
  organiser: string;
  organisingAssociation: string;
  contactPerson: string;
  telephoneNo: string;
  address: string;
  zipCode: string;
  location: string;
  organiserEmail: string;
  competitionDirectorEmail: string;
  website: string;
}

/** All fields empty — the safe default when `extra_json` has no organiser section. */
export const EMPTY_ORGANISER_DETAILS: Readonly<OrganiserDetails> = {
  organiser: '',
  organisingAssociation: '',
  contactPerson: '',
  telephoneNo: '',
  address: '',
  zipCode: '',
  location: '',
  organiserEmail: '',
  competitionDirectorEmail: '',
  website: '',
};

/** Field present in the organiser section used to pick it out from `extra_json`. */
export const ORGANISER_SECTION_MARKER_HINT = 'Organiser';

/**
 * `field_hint` -> `OrganiserDetails` key. Hints are fixed strings from the
 * API (including the curly apostrophe and the "Competetion" typo below) —
 * match them exactly.
 */
export const ORGANISER_FIELD_HINT_MAP: Readonly<Record<string, keyof OrganiserDetails>> = {
  Organiser: 'organiser',
  'Organising Association': 'organisingAssociation',
  'Contact Person': 'contactPerson',
  'Telephone No': 'telephoneNo',
  Address: 'address',
  'Zip Code': 'zipCode',
  Location: 'location',
  'Organiser’s Email': 'organiserEmail',
  'Competetion Director Email': 'competitionDirectorEmail',
  'Internet Address to the organiser website': 'website',
};

/** Minimal `extra_json` shape `extractOrganiserDetails()` needs from an event/list item. */
export interface ExtraJsonSource {
  extra_json?: {
    /** Optional: section is published only when this key is present and `true`. */
    is_section_publish?: boolean;
    section_json_with_values?: { field_hint?: string; field_value?: string | null }[];
  }[];
}

function findOrganiserSection(item: ExtraJsonSource | null | undefined) {
  const sections = Array.isArray(item?.extra_json) ? item.extra_json : [];
  return sections.find((s) => s.section_json_with_values?.some((f) => f.field_hint === ORGANISER_SECTION_MARKER_HINT));
}

/**
 * Single shared parser for the organiser section of `item.extra_json`: find
 * the section containing `ORGANISER_SECTION_MARKER_HINT`, then map its
 * fields through `ORGANISER_FIELD_HINT_MAP`. Callers that render on every
 * change-detection pass (e.g. a template method call) should cache the
 * result themselves keyed by `item`'s identity — this does no caching.
 *
 * TODO(cleanup): `getLocation()` (all-events.component.ts) and the venue
 * lookup in events-home.component.ts#applyEventBanner hand-roll this same
 * marker-scan shape; worth a shared `findExtraJsonSection()` helper later.
 */
export function extractOrganiserDetails(item: ExtraJsonSource | null | undefined): OrganiserDetails {
  const details: OrganiserDetails = { ...EMPTY_ORGANISER_DETAILS };

  findOrganiserSection(item)?.section_json_with_values?.forEach(({ field_hint, field_value }) => {
    const key = field_hint && ORGANISER_FIELD_HINT_MAP[field_hint];
    if (key) {
      details[key] = field_value ?? '';
    }
  });

  return details;
}

/**
 * Shared "is there anything to show" check for the organiser details
 * section — used by both the Events Home defer-gate and
 * `EventOrganizerDetailsComponent`'s own visibility check, so the two
 * conditions can't drift apart if a new field is ever added here.
 */
export function hasAnyOrganiserDetail(details: OrganiserDetails): boolean {
  return !!(details.organiser || details.telephoneNo || details.organiserEmail);
}

/**
 * The organiser section is shown only when its `extra_json` entry carries
 * `is_section_publish: true`. A missing key or any other value hides it.
 */
export function isOrganiserSectionPublished(item: ExtraJsonSource | null | undefined): boolean {
  return findOrganiserSection(item)?.is_section_publish === true;
}
